import { defineStore } from 'pinia'
import { ref, toRaw } from 'vue'
import { http } from '@/helpers/http'
import { useIDBKeyval } from '@vueuse/integrations/useIDBKeyval'
import type {
  TTagHint,
  TNiche,
  TVariantsWithNicheImage,
  TLastTagImageUpdate,
  TTagList,
  TTagHintList,
  TVariantsWithNicheImageListSet,
  TLastTagImageUpdateList,
} from '@/types/tags'
import type { TDropdownOption } from '@/types/dropdown'

export const useTagsStore = defineStore('tags', () => {
  const hints = ref<TTagHint[]>([])
  const niches = ref<TNiche[]>([])
  const lastTagImageUpdates = ref<TLastTagImageUpdate[]>([])
  const variantNicheImagesByTemplate = ref<TVariantsWithNicheImageListSet>({})
  const nicheDropdownOptions = ref<TDropdownOption[]>([])
  const { data: dataUpdatesIDB, set: setUpdatesIDB } = useIDBKeyval<TLastTagImageUpdateList>('lastTagImageUpdates', [])
  const { data: dataHintsIDB, set: setHintsIDB } = useIDBKeyval<TTagHintList>('hints', [])
  const { data: dataNichesIDB, set: setNichesIDB } = useIDBKeyval<TNiche[]>('niches', [])
  const { data: dataVariantNicheImagesIDB, set: setVariantNicheImagesIDB } =
    useIDBKeyval<TVariantsWithNicheImageListSet>('variantNicheImagesByTemplate', {})
  const variantNicheImageRequests = new Map<number, Promise<TVariantsWithNicheImage[]>>()

  let fetched: boolean = false
  let nichesFetched: boolean = false
  let lastTagImageUpdatesFetched: boolean = false

  async function fetchTags() {
    if (fetched) {
      return
    }
    try {
      const res = await http.post<TTagHint[]>('/tags/hints')
      if (!res) throw new Error('Failed to fetch themes')
      hints.value = res
      fetched = true
    } catch {
      hints.value = []
    }
  }

  async function storeLastTagImageUpdatesInDbIfNotSaved(forceIdbUpdate?: boolean) {
    if (!dataUpdatesIDB.value?.length || forceIdbUpdate || typeof dataUpdatesIDB.value === 'string') {
      await setUpdatesIDB(lastTagImageUpdates.value)
    }
  }

  async function fetchLastTagImageUpdates(forceIdbUpdate?: boolean): Promise<void> {
    if (lastTagImageUpdatesFetched && !forceIdbUpdate) {
      return
    }
    try {
      const res: TLastTagImageUpdate[] | null = await http.get<TLastTagImageUpdate[]>('/get-last-tag-image-updates/')
      if (!res) throw new Error('Failed to fetch last tag image updates')
      lastTagImageUpdates.value = res
      lastTagImageUpdatesFetched = true
      await storeLastTagImageUpdatesInDbIfNotSaved(forceIdbUpdate)
    } catch {
      lastTagImageUpdates.value = []
    }
  }

  function getLastUpdateEntryByTagName(entries: TLastTagImageUpdate[], tagName: string): TLastTagImageUpdate | null {
    const filteredEntries = entries.filter((item) => item.type === tagName)
    return filteredEntries?.[0] || null
  }

  function isIDBNotActual(
    lastUpdateDB: TLastTagImageUpdate | null,
    lastUpdateIDB: TLastTagImageUpdate | null,
  ): boolean {
    if (!lastUpdateDB || !lastUpdateIDB) {
      return false
    }
    return lastUpdateDB.updated_at !== lastUpdateIDB.updated_at
  }

  async function actualizeIDBData(lastUpdateDB: TLastTagImageUpdate | null, key: string): Promise<void> {
    if (!lastUpdateDB) {
      return
    }

    try {
      const lastUpdates = dataUpdatesIDB.value

      lastUpdates.forEach((item: TLastTagImageUpdate) => {
        if (item.type === key) {
          item.updated_at = lastUpdateDB.updated_at
        }
      })

      await setUpdatesIDB(lastUpdates)
    } catch {
      throw new Error('Failed to parsing last updates')
    }
  }

  async function requestVariantNicheImages(templateId: number): Promise<TVariantsWithNicheImage[]> {
    try {
      const list = (await http.post<TVariantsWithNicheImage[]>('/tags/variant_niche_images', { templateId })) ?? []
      variantNicheImagesByTemplate.value[templateId] = list

      return list
    } catch {
      throw new Error('Failed to fetch variant niche images')
    } finally {
      variantNicheImageRequests.delete(templateId)
    }
  }

  function fetchVariantNicheImages(templateId: number): Promise<TVariantsWithNicheImage[]> {
    const loaded = variantNicheImagesByTemplate.value[templateId]
    if (loaded) {
      return Promise.resolve(loaded)
    }

    const pending = variantNicheImageRequests.get(templateId)
    if (pending) {
      return pending
    }

    const request = requestVariantNicheImages(templateId)
    variantNicheImageRequests.set(templateId, request)

    return request
  }

  async function cacheVariantNicheImages(
    templateId: number,
    list: TVariantsWithNicheImage[],
    dropOtherTemplates: boolean,
  ): Promise<void> {
    const current = dataVariantNicheImagesIDB.value
    const cache: TVariantsWithNicheImageListSet =
      dropOtherTemplates || typeof current !== 'object' ? {} : { ...toRaw(current) }
    const entries = Object.keys(cache)
    //keep max 10 entries in indexedDB
    if (entries.length > 9) {
      delete cache[Number(entries[entries.length - 1])]
    }
    cache[templateId] = toRaw(list)

    await setVariantNicheImagesIDB(cache)
  }

  async function loadVariantNicheImages(templateId: number, cacheOutdated: boolean): Promise<void> {
    const cached = cacheOutdated ? undefined : dataVariantNicheImagesIDB.value?.[templateId]
    if (cached) {
      variantNicheImagesByTemplate.value[templateId] = cached
      return
    }

    const list = await fetchVariantNicheImages(templateId)
    await cacheVariantNicheImages(templateId, list, cacheOutdated)
  }

  async function refreshVariantNicheImages(templateId: number): Promise<void> {
    delete variantNicheImagesByTemplate.value[templateId]
    const list = await requestVariantNicheImages(templateId)
    await cacheVariantNicheImages(templateId, list, false)
  }

  async function getDataFromLocalStorageOrDb(key: string, templateId?: number): Promise<void> {
    let valueFromIDB: string | TTagList | TNiche[] | TTagHintList | TVariantsWithNicheImageListSet = ''

    if (key === 'hints') {
      valueFromIDB = dataHintsIDB.value
    } else if (key === 'niches') {
      valueFromIDB = dataNichesIDB.value
    } else if (key === 'variantNicheImages') {
      valueFromIDB = dataVariantNicheImagesIDB.value
    }

    const noIDBData =
      typeof valueFromIDB === 'string' || Array.isArray(valueFromIDB)
        ? valueFromIDB.length === 0
        : Object.keys(valueFromIDB ?? {}).length === 0
    const lastUpdates = dataUpdatesIDB.value
    if (typeof dataUpdatesIDB.value === 'string') {
      await fetchLastTagImageUpdates(true)
    }

    const lastUpdateDB = getLastUpdateEntryByTagName(lastTagImageUpdates.value, key)
    const lastUpdateIDB = getLastUpdateEntryByTagName(lastUpdates, key)
    const iDBNotActual = isIDBNotActual(lastUpdateIDB, lastUpdateDB)

    try {
      if (key === 'hints') {
        if (noIDBData || iDBNotActual || typeof valueFromIDB === 'string') {
          await fetchTags()
          await setHintsIDB(hints.value)
        } else {
          hints.value = dataHintsIDB.value
        }
      } else if (key === 'niches') {
        if (noIDBData || iDBNotActual || typeof valueFromIDB === 'string') {
          await fetchNiches()
          await setNichesIDB(niches.value)
        } else {
          niches.value = dataNichesIDB.value
          nicheDropdownOptions.value = niches.value
            .filter((niche) => niche?.tag_images.length > 0)
            .map((niche) => ({
              name: niche.name,
              label: niche.name,
            }))
        }
      } else if (key === 'variantNicheImages' && templateId) {
        await loadVariantNicheImages(templateId, iDBNotActual)
      }
      if (iDBNotActual) {
        await actualizeIDBData(lastUpdateDB, key)
      }
    } catch (error) {
      console.error('Error fetching or parsing data:', error)
    }
  }

  async function fetchNiches() {
    if (nichesFetched) {
      return
    }
    try {
      niches.value = await http.get<TNiche[]>('/tags/get_templates_tags_with_images')
      nicheDropdownOptions.value = niches.value
        .filter((niche) => niche?.tag_images.length > 0)
        .map((niche) => ({
          name: niche.name,
          label: niche.name,
        }))
      nichesFetched = true
    } catch {
      throw new Error('Failed to fetch niches')
    }
  }

  return {
    hints,
    niches,
    variantNicheImagesByTemplate,
    nicheDropdownOptions,
    actualizeIDBData,
    isIDBNotActual,
    getLastUpdateEntryByTagName,
    fetchTags,
    fetchVariantNicheImages,
    refreshVariantNicheImages,
    getDataFromLocalStorageOrDb,
    fetchLastTagImageUpdates,
    lastTagImageUpdates,
  }
})
